<template>
  <div class="nr1-questionnaire-container">
    <div v-if="loading" class="text-center py-5">
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem"
      ></b-spinner>
      <p class="mt-3 text-muted">Carregando questionário...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      <i class="fa fa-exclamation-triangle"></i>
      {{ error }}
    </div>

    <div v-else-if="submitted" class="submission-success-wrap py-5">
      <div class="submission-success-card text-center">
        <div class="submission-success-icon">
          <i class="fa fa-check"></i>
        </div>

        <h3 class="submission-success-title">
          Obrigado pela sua participação!
        </h3>

        <p class="submission-success-subtitle mb-0">
          Suas respostas foram registradas de forma anônima e confidencial.
        </p>

        <div v-if="submissionProtocol" class="submission-protocol-card mt-4">
          <p class="submission-protocol-label mb-1">Protocolo de Registro</p>
          <h4 class="submission-protocol-value mb-0">
            {{ submissionProtocol }}
          </h4>
        </div>
      </div>
    </div>

    <div v-else class="questionnaire-form">
      <b-card>
        <template #header>
          <h4 class="mb-0">
            <i class="fa fa-clipboard-check mr-2"></i>
            {{ questionnaire.title }}
          </h4>
        </template>

        <div v-if="questionnaire.description" class="alert alert-info mb-4">
          <i class="fa fa-info-circle"></i>
          {{ questionnaire.description }}
        </div>

        <div class="stepper-header mb-4">
          <div class="stepper-track mb-3">
            <div class="stepper-item active">
              <div class="stepper-circle">{{ currentStep + 1 }}</div>
              <span class="stepper-label">
                Etapa atual: {{ currentStepLabel }} ({{ currentStep + 1 }}/{{
                  totalSteps
                }})
              </span>
            </div>
          </div>

          <div v-if="questionnaire.categories.length > 0">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <small class="text-muted font-weight-bold">
                Progresso das categorias
              </small>
              <small class="text-muted">
                {{ completedCategoriesCount }}/{{
                  questionnaire.categories.length
                }}
                concluídas
              </small>
            </div>
            <b-progress
              :value="progressPercent"
              max="100"
              height="10px"
              variant="primary"
            ></b-progress>
          </div>
        </div>

        <div v-if="isConsentStep">
          <div class="consent-box mb-4">
            <div
              class="consent-text"
              v-html="questionnaire.consentText || defaultConsentText"
            ></div>
            <b-form-checkbox v-model="consentGiven" class="mt-3">
              <strong>
                Li e concordo com os termos acima e com o uso das minhas
                respostas para fins de avaliação de riscos ocupacionais
              </strong>
            </b-form-checkbox>
          </div>

          <div
            v-if="!hasValidToken"
            class="sector-selection mb-4 p-3 border rounded bg-light"
          >
            <b-form-group
              label="Informe seu setor de trabalho *"
              label-for="area-select"
              class="mb-0"
              description="Isso nos ajuda a tabular os riscos por área de forma totalmente anônima."
            >
              <b-form-select
                id="area-select"
                v-model="selectedArea"
                :options="areaOptions"
                :disabled="!areaOptions.length"
                required
              >
                <template #first>
                  <b-form-select-option :value="null" disabled
                    >-- Selecione seu setor --</b-form-select-option
                  >
                </template>
              </b-form-select>
              <small v-if="!areaOptions.length" class="text-danger">
                Nenhum setor cadastrado em Parâmetros Gerais para este
                questionário.
              </small>
            </b-form-group>
          </div>

          <div
            v-if="enabledProfileFields.length"
            class="profile-collection-box mb-4 p-3 border rounded bg-light"
          >
            <h5 class="mb-2">Informações complementares</h5>
            <p class="text-muted mb-3">
              Preencha os dados abaixo conforme a configuração deste
              questionário.
            </p>

            <b-row>
              <b-col
                v-for="field in enabledProfileFields"
                :key="field.key"
                md="6"
              >
                <b-form-group
                  :label="`${getProfileFieldLabel(field)} *`"
                  :description="field.description"
                >
                  <b-form-input
                    v-if="field.type === 'text'"
                    v-model.trim="respondentProfile[field.key]"
                    :maxlength="field.maxLength"
                    :placeholder="field.placeholder"
                    @input="stepValidationMessage = null"
                  ></b-form-input>

                  <b-form-select
                    v-else
                    v-model="respondentProfile[field.key]"
                    :options="getProfileFieldOptions(field)"
                    @change="stepValidationMessage = null"
                  >
                    <template #first>
                      <b-form-select-option :value="null" disabled>
                        -- Selecione --
                      </b-form-select-option>
                    </template>
                  </b-form-select>

                  <div
                    v-if="field.type === 'text'"
                    class="text-right text-muted small mt-1"
                  >
                    {{ getRespondentProfileLength(field.key) }}/{{
                      field.maxLength
                    }}
                  </div>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </div>

        <div v-else-if="isQualitativeStep">
          <div class="qualitative-step mb-4">
            <h5 class="mb-3">Relato final opcional</h5>
            <p class="text-muted mb-3">
              Você gostaria de relatar algum risco, situação não contemplada ou
              sugestão de melhoria?
            </p>

            <b-form-textarea
              v-model="qualitativeComment"
              rows="5"
              :maxlength="qualitativeMaxLength"
              placeholder="Escreva aqui, se desejar..."
            ></b-form-textarea>

            <div class="text-right text-muted small mt-2">
              {{ qualitativeCommentLength }}/{{ qualitativeMaxLength }}
            </div>
          </div>
        </div>

        <div v-else>
          <div
            v-if="currentCategory"
            :key="currentCategory.id || currentCategory._id"
            class="category-section mb-4"
          >
            <div class="category-header">
              <h5>
                <b-badge
                  :variant="getSeverityVariant(currentCategory.severity)"
                >
                  {{ currentCategory.name }}
                </b-badge>
              </h5>
              <p v-if="currentCategory.description" class="text-muted">
                {{ currentCategory.description }}
              </p>
            </div>

            <div class="questions-list mt-3">
              <div
                v-for="(question, qIndex) in currentCategory.questions"
                :key="question.id || question._id"
                class="question-item mb-4"
              >
                <label class="question-label">
                  {{ qIndex + 1 }}. {{ question.text }}
                  <span v-if="question.required" class="text-danger">*</span>
                </label>

                <div class="likert-scale">
                  <div class="scale-options">
                    <label
                      v-for="option in [
                        { value: 1, label: 'Nunca' },
                        { value: 2, label: 'Raramente' },
                        { value: 3, label: 'Às vezes' },
                        { value: 4, label: 'Frequentemente' },
                        { value: 5, label: 'Sempre' },
                      ]"
                      :key="option.value"
                      class="scale-option"
                      :class="{
                        selected:
                          getAnswer(currentCategoryIndex, qIndex) ===
                          option.value,
                      }"
                    >
                      <input
                        type="radio"
                        :name="`q_${currentCategoryIndex}_${qIndex}`"
                        :value="option.value"
                        @change="
                          setAnswer(currentCategoryIndex, qIndex, option.value)
                        "
                      />
                      <span class="scale-number">{{ option.value }}</span>
                      <small class="scale-option-label">{{
                        option.label
                      }}</small>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="alert alert-warning">
            Não foi possível identificar a categoria atual.
          </div>
        </div>

        <div class="d-flex justify-content-between mt-4">
          <b-button :disabled="isFirstStep || submitting" @click="previousStep">
            <i class="fa fa-arrow-left mr-2"></i>
            Voltar
          </b-button>

          <b-button
            v-if="!isLastStep"
            variant="primary"
            :disabled="submitting"
            @click="nextStep"
          >
            Próximo
            <i class="fa fa-arrow-right ml-2"></i>
          </b-button>

          <b-button
            v-else
            variant="primary"
            size="lg"
            :disabled="submitting || !isFormValid"
            @click="submitResponses"
          >
            <b-spinner v-if="submitting" small class="mr-2"></b-spinner>
            <i v-else class="fa fa-paper-plane mr-2"></i>
            {{ submitting ? "Enviando..." : "Enviar Respostas" }}
          </b-button>
        </div>

        <div v-if="stepValidationMessage" class="alert alert-warning mt-3 mb-0">
          <i class="fa fa-exclamation-circle mr-2"></i>
          {{ stepValidationMessage }}
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";

const NR1_SEX_OPTIONS = ["Masculino", "Feminino"];
const NR1_GENDER_DIVERSITY_ONLY_OPTIONS = [
  "Não-binário",
  "Agênero",
  "Prefiro não informar",
  "Outro",
];
const NR1_GENDER_DIVERSITY_OPTIONS = [
  ...NR1_SEX_OPTIONS,
  ...NR1_GENDER_DIVERSITY_ONLY_OPTIONS,
];

const PROFILE_COLLECTION_FIELDS = [
  {
    key: "jobFunction",
    label: "Função",
    type: "text",
    maxLength: 160,
    placeholder: "Informe sua função",
    description: "Informe a função que você exerce atualmente.",
  },
  {
    key: "ageRange",
    label: "Faixa etária",
    type: "select",
    options: [
      "Até 24 anos",
      "25 a 34 anos",
      "35 a 44 anos",
      "45 a 54 anos",
      "55 anos ou mais",
    ],
  },
  {
    key: "sex",
    label: "Sexo",
    type: "select",
    options: NR1_SEX_OPTIONS,
  },
  {
    key: "tenureRange",
    label: "Tempo de empresa",
    type: "select",
    options: [
      "Menos de 1 ano",
      "2 a 5 anos",
      "5 a 10 anos",
      "10 a 15 anos",
      "15 a 20 anos",
      "Mais de 20 anos",
    ],
  },
  {
    key: "workLocation",
    label: "Local de realização das atividades",
    type: "select",
    options: [
      "Na empresa",
      "Parte na empresa e parte em casa",
      "Somente em casa (remoto)",
      "Em campo/externo",
    ],
  },
];

function buildDefaultProfileCollection() {
  return {
    jobFunction: { enabled: false },
    ageRange: { enabled: false },
    sex: { enabled: false, diverse: false },
    tenureRange: { enabled: false },
    workLocation: { enabled: false },
  };
}

function buildEmptyRespondentProfile() {
  return {
    jobFunction: "",
    ageRange: null,
    sex: null,
    tenureRange: null,
    workLocation: null,
  };
}

function normalizeProfileCollection(profileCollection) {
  const defaults = buildDefaultProfileCollection();

  return Object.keys(defaults).reduce((result, key) => {
    const enabled =
      profileCollection &&
      profileCollection[key] &&
      profileCollection[key].enabled === true;

    if (key === "sex") {
      result[key] = {
        enabled,
        diverse:
          profileCollection &&
          profileCollection[key] &&
          profileCollection[key].diverse === true,
      };
      return result;
    }

    result[key] = { enabled };
    return result;
  }, {});
}

export default {
  name: "NR1Questionnaire",
  data() {
    return {
      loading: true,
      error: null,
      questionnaire: null,
      consentGiven: false,
      answers: [],
      submitting: false,
      submitted: false,
      submissionProtocol: null,
      token: null,
      availableAreas: [],
      selectedArea: null,
      respondentProfile: buildEmptyRespondentProfile(),
      qualitativeComment: "",
      qualitativeMaxLength: 600,
      currentStep: 0,
      stepValidationMessage: null,
      defaultConsentText:
        "Ao prosseguir, você concorda com o uso anônimo das suas respostas para fins de avaliação de riscos ocupacionais.",
    };
  },
  computed: {
    hasValidToken() {
      return (
        !!this.token && this.token !== "undefined" && this.token !== "null"
      );
    },
    normalizedProfileCollection() {
      return normalizeProfileCollection(
        this.questionnaire && this.questionnaire.profileCollection
      );
    },
    enabledProfileFields() {
      return PROFILE_COLLECTION_FIELDS.filter(
        (field) => this.normalizedProfileCollection[field.key].enabled
      );
    },
    steps() {
      if (!this.questionnaire) return [];

      return [
        {
          id: "consent",
          label: "Consentimento",
          type: "consent",
        },
        ...this.questionnaire.categories.map((category, index) => ({
          id: category.id || category._id || `category-${index}`,
          label: category.name,
          type: "category",
          categoryIndex: index,
        })),
        {
          id: "qualitative-comment",
          label: "Relato Final",
          type: "qualitative",
        },
      ];
    },
    totalSteps() {
      return this.steps.length;
    },
    currentStepLabel() {
      if (!this.steps[this.currentStep]) return "";
      return this.steps[this.currentStep].label;
    },
    isConsentStep() {
      return this.steps[this.currentStep]?.type === "consent";
    },
    isQualitativeStep() {
      return this.steps[this.currentStep]?.type === "qualitative";
    },
    currentCategoryIndex() {
      const step = this.steps[this.currentStep];
      return step?.type === "category" ? step.categoryIndex : -1;
    },
    currentCategory() {
      if (!this.questionnaire || this.currentCategoryIndex < 0) return null;
      return this.questionnaire.categories[this.currentCategoryIndex] || null;
    },
    isFirstStep() {
      return this.currentStep === 0;
    },
    isLastStep() {
      return this.currentStep === this.steps.length - 1;
    },
    completedCategoriesCount() {
      if (!this.questionnaire) return 0;

      return this.questionnaire.categories.filter((_, catIndex) =>
        this.isCategoryStepValid(catIndex)
      ).length;
    },
    progressPercent() {
      if (!this.questionnaire || this.questionnaire.categories.length === 0) {
        return 0;
      }

      return Math.round(
        (this.completedCategoriesCount / this.questionnaire.categories.length) *
          100
      );
    },
    isFormValid() {
      if (!this.questionnaire) return false;
      if (!this.consentGiven) return false;

      if (!this.hasValidToken && !this.selectedArea) return false;
      if (!this.isRespondentProfileValid()) return false;

      for (
        let catIndex = 0;
        catIndex < this.questionnaire.categories.length;
        catIndex++
      ) {
        if (!this.isCategoryStepValid(catIndex)) return false;
      }

      if (this.qualitativeCommentLength > this.qualitativeMaxLength) {
        return false;
      }

      return true;
    },
    qualitativeCommentLength() {
      return this.qualitativeComment.length;
    },
    areaOptions() {
      if (!this.availableAreas) return [];
      return this.availableAreas.map((area) => ({
        value: area,
        text: area,
      }));
    },
  },
  async mounted() {
    const routeToken = this.$route.query.token;
    this.token =
      routeToken && routeToken !== "undefined" && routeToken !== "null"
        ? routeToken
        : null;
    if (!this.token && !this.$route.params.id) {
      this.error = "Token ou ID do questionário não fornecido";
      this.loading = false;
      return;
    }
    await this.loadQuestionnaire();
  },
  methods: {
    async loadQuestionnaire() {
      try {
        this.loading = true;
        this.error = null;

        const params = {};
        if (this.hasValidToken) {
          params.token = this.token;
        }

        const questionnaireId = this.$route.params.id;
        const response = await request({
          url: `/nr1/public/questionnaire/${questionnaireId}`,
          method: "get",
          params,
        });

        this.questionnaire = {
          ...response.data,
          profileCollection: normalizeProfileCollection(
            response.data.profileCollection
          ),
          categories: (response.data.categories || [])
            .map((category) => ({
              ...category,
              questions: (category.questions || []).filter(
                (question) => (question?.responseType || "scale") !== "text"
              ),
            }))
            .filter((category) => category.questions.length > 0),
        };
        this.availableAreas = response.data.availableAreas || [];
        this.currentStep = 0;
        this.qualitativeComment = "";
        this.respondentProfile = buildEmptyRespondentProfile();

        this.answers = this.questionnaire.categories.map((cat) =>
          new Array(cat.questions.length).fill(null)
        );
      } catch (err) {
        console.error("Error loading questionnaire:", err);
        this.error =
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : "Erro ao carregar questionário";
      } finally {
        this.loading = false;
      }
    },
    getAnswer(catIndex, qIndex) {
      if (!this.answers[catIndex]) return null;
      return this.answers[catIndex][qIndex];
    },
    setAnswer(catIndex, qIndex, value) {
      if (!this.answers[catIndex]) {
        this.$set(this.answers, catIndex, []);
      }
      this.$set(this.answers[catIndex], qIndex, value);
      this.stepValidationMessage = null;
    },
    getProfileFieldOptions(field) {
      const options =
        field.key === "sex" && this.normalizedProfileCollection?.sex?.diverse
          ? NR1_GENDER_DIVERSITY_OPTIONS
          : field.options || [];

      return options.map((option) => ({
        value: option,
        text: option,
      }));
    },
    getProfileFieldLabel(field) {
      if (
        field.key === "sex" &&
        this.normalizedProfileCollection?.sex?.diverse
      ) {
        return "Gênero";
      }

      return field.label;
    },
    getRespondentProfileLength(fieldKey) {
      const value = this.respondentProfile[fieldKey];
      return typeof value === "string" ? value.length : 0;
    },
    isRespondentProfileValid(setMessage = false) {
      for (const field of this.enabledProfileFields) {
        const rawValue = this.respondentProfile[field.key];

        if (field.type === "text") {
          const normalizedValue =
            typeof rawValue === "string" ? rawValue.trim() : "";

          if (!normalizedValue) {
            if (setMessage) {
              this.stepValidationMessage = `${this.getProfileFieldLabel(
                field
              )} é obrigatório(a).`;
            }
            return false;
          }

          if (field.maxLength && normalizedValue.length > field.maxLength) {
            if (setMessage) {
              this.stepValidationMessage = `${this.getProfileFieldLabel(
                field
              )} deve ter no máximo ${field.maxLength} caracteres.`;
            }
            return false;
          }

          continue;
        }

        if (!rawValue) {
          if (setMessage) {
            this.stepValidationMessage = `${this.getProfileFieldLabel(
              field
            )} é obrigatório(a).`;
          }
          return false;
        }
      }

      return true;
    },
    buildRespondentProfilePayload() {
      const payload = {};

      for (const field of this.enabledProfileFields) {
        const rawValue = this.respondentProfile[field.key];
        payload[field.key] =
          typeof rawValue === "string" ? rawValue.trim() : rawValue;
      }

      return payload;
    },
    isCategoryStepValid(catIndex) {
      if (!this.questionnaire) return false;
      const category = this.questionnaire.categories[catIndex];
      if (!category) return false;

      for (let qIndex = 0; qIndex < category.questions.length; qIndex++) {
        const question = category.questions[qIndex];
        if (question.required) {
          const answer = this.getAnswer(catIndex, qIndex);
          if (!answer || answer < 1 || answer > 5) {
            return false;
          }
        }
      }

      return true;
    },
    isCurrentStepValid() {
      if (this.isConsentStep) {
        if (!this.consentGiven) {
          this.stepValidationMessage =
            "Você precisa aceitar o consentimento para continuar.";
          return false;
        }

        if (!this.hasValidToken && this.areaOptions.length === 0) {
          this.stepValidationMessage =
            "Nenhum setor está disponível. Solicite o cadastro em Parâmetros Gerais da empresa.";
          return false;
        }

        if (!this.hasValidToken && !this.selectedArea) {
          this.stepValidationMessage =
            "Informe seu setor de trabalho para continuar.";
          return false;
        }

        if (!this.isRespondentProfileValid(true)) {
          return false;
        }

        return true;
      }

      if (this.isQualitativeStep) {
        if (this.qualitativeCommentLength > this.qualitativeMaxLength) {
          this.stepValidationMessage =
            "O relato final deve ter no máximo 600 caracteres.";
          return false;
        }

        return true;
      }

      const category = this.currentCategory;
      if (!category) {
        this.stepValidationMessage =
          "Não foi possível carregar a categoria atual.";
        return false;
      }

      const isValid = this.isCategoryStepValid(this.currentCategoryIndex);
      if (!isValid) {
        this.stepValidationMessage =
          "Responda todas as perguntas obrigatórias desta categoria para continuar.";
      }
      return isValid;
    },
    nextStep() {
      this.stepValidationMessage = null;

      if (!this.isCurrentStepValid()) {
        this.$notify({
          type: "warning",
          title: "Atenção",
          text: this.stepValidationMessage,
        });
        return;
      }

      if (!this.isLastStep) {
        this.currentStep += 1;
      }
    },
    previousStep() {
      this.stepValidationMessage = null;
      if (!this.isFirstStep) {
        this.currentStep -= 1;
      }
    },
    isStepCompleted(stepIndex) {
      if (stepIndex === 0) {
        return (
          this.consentGiven &&
          (this.hasValidToken || !!this.selectedArea) &&
          this.isRespondentProfileValid()
        );
      }

      return this.isCategoryStepValid(stepIndex - 1);
    },
    getSeverityVariant(severity) {
      const variants = {
        1: "success",
        2: "info",
        3: "warning",
        4: "danger",
      };
      return variants[severity] || "secondary";
    },
    async submitResponses() {
      if (!this.isFormValid) {
        this.$notify({
          type: "warning",
          title: "Atenção",
          text: "Por favor, responda todas as perguntas obrigatórias",
        });
        return;
      }

      this.submitting = true;
      try {
        const formattedAnswers = [];
        for (
          let catIndex = 0;
          catIndex < this.questionnaire.categories.length;
          catIndex++
        ) {
          const category = this.questionnaire.categories[catIndex];
          for (let qIndex = 0; qIndex < category.questions.length; qIndex++) {
            const question = category.questions[qIndex];
            const value = this.getAnswer(catIndex, qIndex);

            if (value) {
              formattedAnswers.push({
                categoryId: category.id || category._id,
                questionId: question.id || question._id,
                responseType: "scale",
                value: value,
              });
            }
          }
        }

        const payload = {
          answers: formattedAnswers,
          qualitativeComment: this.qualitativeComment.trim(),
          area: this.selectedArea,
          respondentProfile: this.buildRespondentProfilePayload(),
          token: this.hasValidToken ? this.token : null,
          metadata: {
            source: this.hasValidToken
              ? "email"
              : this.$route.name === "NR1 Questionnaire Direct"
              ? "qrcode"
              : "web",
            userAgent: navigator.userAgent,
          },
        };

        const result = await request({
          url: `/nr1/public/questionnaire/${this.$route.params.id}/response`,
          method: "post",
          data: payload,
          params: this.hasValidToken ? { token: this.token } : {},
        });

        this.submissionProtocol = result.data.protocol;
        this.submitted = true;
      } catch (err) {
        console.error("Error submitting responses:", err);
        this.$notify({
          type: "error",
          title: "Erro",
          text:
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Erro ao enviar respostas",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.nr1-questionnaire-container {
  max-width: 900px;
  margin: 2rem auto;
  padding: 1rem;
}

.submission-success-wrap {
  display: flex;
  justify-content: center;
}

.submission-success-card {
  width: 100%;
  max-width: 560px;
  padding: 2.25rem 1.75rem;
  border-radius: 16px;
  background: linear-gradient(155deg, #ffffff 0%, #f4fbf7 100%);
  border: 1px solid #dcefe4;
  box-shadow: 0 14px 30px rgba(12, 70, 46, 0.1);
}

.submission-success-icon {
  width: 88px;
  height: 88px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 30% 30%, #66d191, #27ae60);
  color: #fff;
  font-size: 2.2rem;
  box-shadow: 0 10px 24px rgba(39, 174, 96, 0.35);
}

.submission-success-title {
  color: #123b2a;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.submission-success-subtitle {
  color: #4a5d54;
  font-size: 1.02rem;
  line-height: 1.6;
}

.submission-protocol-card {
  display: inline-block;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border: 1px dashed #7bc79b;
  background: #ecf9f1;
}

.submission-protocol-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #2f6c4f;
}

.submission-protocol-value {
  color: #15563c;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.stepper-track {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}

.stepper-item {
  display: inline-flex;
  align-items: center;
  border: 1px solid #dee2e6;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  background: #f8f9fa;
  text-align: center;
}

.stepper-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  line-height: 24px;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  background: #dee2e6;
  color: #495057;
  margin-right: 0.5rem;
}

.stepper-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
}

.stepper-item.active {
  border-color: #007bff;
  background: #eef6ff;
}

.stepper-item.active .stepper-circle {
  background: #007bff;
  color: #fff;
}

.stepper-item.active .stepper-label {
  color: #007bff;
}

.stepper-item.completed {
  border-color: #28a745;
  background: #eef9f1;
}

.stepper-item.completed .stepper-circle {
  background: #28a745;
  color: #fff;
}

.stepper-item.completed .stepper-label {
  color: #1e7e34;
}

.consent-box {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1.5rem;
}

.profile-collection-box {
  background: #f8f9fa;
}

.consent-text {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #495057;
}

.category-section {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  padding: 1.5rem;
}

.category-header {
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
}

.question-item {
  padding: 1rem;
  background: #fafafa;
  border-radius: 0.25rem;
}

.question-label {
  font-weight: 500;
  color: #333;
  display: block;
  margin-bottom: 1rem;
}

.likert-scale {
  margin-top: 0.75rem;
}

.scale-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
}

.scale-options {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.scale-option {
  flex: 1;
  text-align: center;
  cursor: pointer;
  position: relative;
}

.scale-option input[type="radio"] {
  position: absolute;
  opacity: 0;
}

.scale-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border: 2px solid #ddd;
  border-radius: 50%;
  transition: all 0.2s;
  background: white;
  font-weight: 600;
  color: #666;
  line-height: 1;
}

.scale-option-label {
  display: block;
  margin-top: 0.45rem;
  color: #666;
  font-size: 0.75rem;
  line-height: 1.2;
}

.scale-option:hover .scale-number {
  border-color: #007bff;
  background: #f0f8ff;
}

.scale-option:hover .scale-option-label {
  color: #007bff;
}

.scale-option.selected .scale-number {
  background: #007bff;
  border-color: #007bff;
  color: white;
  transform: scale(1.08);
}

.scale-option.selected .scale-option-label {
  color: #007bff;
  font-weight: 600;
}

@media (max-width: 768px) {
  .submission-success-card {
    padding: 1.75rem 1.15rem;
    border-radius: 12px;
  }

  .submission-success-icon {
    width: 72px;
    height: 72px;
    font-size: 1.8rem;
  }

  .submission-success-title {
    font-size: 1.45rem;
  }

  .submission-success-subtitle {
    font-size: 0.95rem;
  }

  .stepper-track {
    gap: 0.35rem;
  }

  .stepper-item {
    padding: 0.25rem 0.55rem;
  }

  .stepper-label {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .scale-number {
    width: 40px;
    height: 40px;
    font-size: 0.9rem;
  }

  .scale-option-label {
    font-size: 0.68rem;
  }

  .scale-options {
    gap: 0.25rem;
  }
}
</style>
